import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Landmark, ClipboardList, Receipt, TrendingUp, Users, FileText,
  Calendar, ChevronRight, Download, FileDown, RefreshCw, X,
} from 'lucide-react';
import { toast } from 'react-toastify';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import Table, { TABLE_THEME } from '../../../components/Table/Table';

import usePayrollEntity from '../../../utils/usePayrollEntity';
// Report menu config (was in data/payrollMockData.js). Each entityKey is a
// payroll entity loaded from the API by the Reports screen.
const PAYROLL_REPORT_CATALOGUE = [
  { id: 'earnings-report', name: 'Earnings Report', description: 'Breakdown of earning components paid out.', entityKey: 'payrollComponents' },
  { id: 'deductions-report', name: 'Deductions Report', description: 'Breakdown of deduction components applied.', entityKey: 'payrollComponents' },
  { id: 'loan-report', name: 'Loan Report', description: 'Outstanding staff loan balances.', entityKey: 'loanManagement' },
  { id: 'payroll-cost-analysis', name: 'Payroll Cost Analysis', description: 'Cost trend across recent periods.', entityKey: 'payrollPeriods' },
];

const FIELD = `${TABLE_THEME.fieldBase} ${TABLE_THEME.fieldFocus}`;
const LABEL = TABLE_THEME.fieldLabel;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const toTitleCase = (key) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
 
const reportIcon = (name) => {
  const n = name.toLowerCase();
  if (n.includes('register') || n.includes('summary')) return ClipboardList;
  if (n.includes('earnings') || n.includes('deduction') || n.includes('component')) return Receipt;
  if (n.includes('bank') || n.includes('payment') || n.includes('transfer')) return Landmark;
  if (n.includes('overtime') || n.includes('loan')) return TrendingUp;
  if (n.includes('department') || n.includes('employee') || n.includes('history')) return Users;
  return FileText;
};

 
const CARD_TONES = [
  { chip: 'bg-primary-50 text-primary-700' },
  { chip: 'bg-blue-50 text-blue-700' },
  { chip: 'bg-amber-50 text-amber-700' },
  { chip: 'bg-purple-50 text-purple-700' },
  { chip: 'bg-rose-50 text-rose-700' },
  { chip: 'bg-teal-50 text-teal-700' },
  { chip: 'bg-yellow-50 text-yellow-700' },
  { chip: 'bg-stone-50 text-stone-700' },
  { chip: 'bg-indigo-50 text-indigo-700' },
  { chip: 'bg-lime-50 text-lime-700' },
  { chip: 'bg-orange-50 text-orange-700' },
  { chip: 'bg-cyan-50 text-cyan-700' },
];
 
const ReportPreviewTable = ({ entityKey }) => {
  const { list, loading } = usePayrollEntity(entityKey);
  const sample = list[0] || {};

  const columns = useMemo(() => {
    const keys = Object.keys(sample).filter((k) => k !== 'id').slice(0, 7);
    return keys.map((k, i) => ({
      key: k,
      label: toTitleCase(k),
      grow: i === 0,
      className: i === 0 ? 'font-semibold text-gray-900' : 'text-gray-700',
      render: (r) => {
        const v = r[k];
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        return v;
      },
    }));
  }, [sample]);

  return (
    <Table
      loading={loading}
      rows={list}
      rowKey="id"
      search
      searchLabel="Search"
      searchKeys={columns.map((c) => c.key)}
      searchPlaceholder="Search this report..."
      emptyMessage="No data available for this report yet."
      pageSize={6}
      scrollClassName=""
      columns={columns}
    />
  );
};

/* ── Page ────────────────────────────────────────────────────────────────── */
const Reports = () => {
  const navigate = useNavigate();
  const [openReport, setOpenReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: 11 }, (_, i) => currentYear - i);

  const regularReports = useMemo(
    () => PAYROLL_REPORT_CATALOGUE.filter((r) => r.entityKey !== 'cFormData'),
    []
  );

  const refresh = () =>
    toast.info(`Refreshing data for ${MONTHS[selectedMonth - 1]} ${selectedYear}`, { autoClose: 2000 });

  const exportReport = (kind) =>
    toast.info(`Exporting report as ${kind}...`, { autoClose: 2000 });

  return (
    <div className="p-6 space-y-4">
      <Breadcrumb
        trail={['Payroll', 'Reports']}
        title="Payroll Reports"
        subtitle="Statutory and management reports generated from the payroll module."
      />

      {/* ── Report period filter bar ─────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-end gap-3 flex-wrap">
          <span className="text-[13px] text-gray-500 font-medium pb-2">Report Period:</span>
          <div className="w-[150px]">
            <span className={LABEL}>Month</span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className={`${FIELD} w-full`}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div className="w-[110px]">
            <span className={LABEL}>Year</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className={`${FIELD} w-full`}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold
            bg-primary-600 text-white hover:bg-primary-700 transition-colors"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* ── Quick stats ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-primary-50 border border-primary-100 rounded-xl p-3">
          <div className="text-[11px] font-medium text-gray-500">Total Reports</div>
          <div className="text-xl font-bold text-primary-700">{regularReports.length + 1}</div>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
          <div className="text-[11px] font-medium text-gray-500">Categories</div>
          <div className="text-xl font-bold text-blue-700">6</div>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
          <div className="text-[11px] font-medium text-gray-500">Available</div>
          <div className="text-xl font-bold text-amber-700">{regularReports.length + 1}</div>
        </div>
        <div className="bg-purple-50 border border-purple-100 rounded-xl p-3">
          <div className="text-[11px] font-medium text-gray-500">Current Period</div>
          <div className="text-base font-bold text-purple-700">{MONTHS[selectedMonth - 1]} {selectedYear}</div>
        </div>
      </div>

      {/* ── Report cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
  {/* EPF C Form */}
  <button
    type="button"
    onClick={() => navigate('/payroll/c-form')}
    className="text-left bg-white rounded-xl p-5 flex flex-col justify-between
      transition-all hover:-translate-y-0.5 hover:shadow-lg"
  >
    <div>
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-50 text-primary-700 mb-3">
        <Landmark size={22} />
      </div>

      <div className="text-[15px] font-bold text-gray-900 mb-1">
        C Form
      </div>

      <div className="text-[13px] text-gray-500 leading-relaxed min-h-[42px]">
        Employee Provident Fund Contribution Report - Form C
      </div>
    </div>

    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary-50 text-primary-700">
        Open Page
      </span>
      <ChevronRight size={16} className="text-primary-700 opacity-60" />
    </div>
  </button>

  {/* ETF K Form */}
  <button
    type="button"
    onClick={() => navigate('/payroll/k-form')}
    className="text-left bg-white rounded-xl p-5 flex flex-col justify-between
      transition-all hover:-translate-y-0.5 hover:shadow-lg"
  >
    <div>
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-50 text-primary-700 mb-3">
        <Landmark size={22} />
      </div>

      <div className="text-[15px] font-bold text-gray-900 mb-1">
        ETF K Form
      </div>

      <div className="text-[13px] text-gray-500 leading-relaxed min-h-[42px]">
        Employee Provident Fund Contribution Report - Form K
      </div>
    </div>

    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary-50 text-primary-700">
        Open Page
      </span>
      <ChevronRight size={16} className="text-primary-700 opacity-60" />
    </div>
  </button>

  {regularReports.map((report, index) => {
    const Icon = reportIcon(report.name);
    const tone = CARD_TONES[index % CARD_TONES.length];

    return (
      <button
        type="button"
        key={report.id}
        onClick={() => setOpenReport(report)}
        className="text-left bg-white rounded-xl p-5 flex flex-col justify-between
          transition-all hover:-translate-y-0.5 hover:shadow-lg"
      >
        <div>
          <div
            className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3 ${tone.chip}`}
          >
            <Icon size={22} />
          </div>

          <div className="text-[15px] font-bold text-gray-900 mb-1 line-clamp-2">
            {report.name}
          </div>

          <div className="text-[13px] text-gray-500 leading-relaxed line-clamp-2 min-h-[42px]">
            {report.description}
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary-50 text-primary-700">
            View Report
          </span>

          <ChevronRight
            size={16}
            className="text-primary-700 opacity-60"
          />
        </div>
      </button>
    );
  })}
</div>

      {/* ── Report preview modal ─────────────────────────────────────────── */}
      {openReport && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8"
          onClick={() => setOpenReport(null)}
        >
          <div
            className="relative w-full max-w-4xl rounded-xl bg-[#FAFAFA] shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-5 pb-4 bg-white border-b border-gray-100 rounded-t-xl">
              <div className="flex items-start gap-3 flex-1 min-w-[200px]">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary-50 text-primary-700 mt-0.5">
                  {(() => { const Icon = reportIcon(openReport.name); return <Icon size={22} />; })()}
                </div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-gray-900 mb-0.5">{openReport.name}</div>
                  <div className="text-[13px] text-gray-500">{openReport.description}</div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border border-gray-200 text-gray-600">
                      <Calendar size={12} />
                      {MONTHS[selectedMonth - 1]} {selectedYear}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border border-gray-200 text-gray-600">
                      {regularReports.length} records
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpenReport(null)}
                aria-label="Close"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5">
              <ReportPreviewTable entityKey={openReport.entityKey} />

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 flex-wrap gap-2">
                <span className="text-[12px] text-gray-500">
                  {regularReports.length} rows · Generated for {MONTHS[selectedMonth - 1]} {selectedYear} on{' '}
                  {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => exportReport('CSV')}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold
                      border border-primary-600 text-primary-700 hover:bg-primary-50 transition-colors"
                  >
                    <Download size={14} />
                    Export CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => exportReport('PDF')}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold
                      bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                  >
                    <FileDown size={14} />
                    Export PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;